import { useState } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable/index";
import { Button } from "@/components/ui/button";
import {
  CommodityOptions,
  findMatchOption,
  generateAccountLabel,
  useListAccounts,
} from "@/lib/api/otc";
import { useListTraders } from "@/lib/api/trader";
import { Trader } from "@/lib/api/reports";
import {
  OtcErrorCorrectionDto,
  useListOtcErrorCorrections,
} from "@/lib/api/otcErrorCorrection";
import { CreateErrorCorrectionModal } from "./CreateErrorCorrectionModal";
import { UpdateErrorCorrectionModal } from "./UpdateErrorCorrectionModal";

function buildColumns(
  tradersById: Map<string, string>,
): DataTableColumn<OtcErrorCorrectionDto>[] {
  return [
    {
      id: "trading_account",
      accessorFn: (row: OtcErrorCorrectionDto) => {
        const accountId = row.TradingAccount?.account_id;
        return accountId || "";
      },
      header: "Trading Account",
    },
    {
      id: "commodity",
      accessorFn: (row: OtcErrorCorrectionDto) =>
        findMatchOption(CommodityOptions, row.commodity)?.label || row.commodity,
      header: "Commodity",
    },
    {
      id: "quantity",
      accessorFn: (row: OtcErrorCorrectionDto) => row.quantity,
      header: "Quantity",
    },
    {
      id: "option_premium",
      accessorFn: (row: OtcErrorCorrectionDto) => row.option_premium,
      header: "Option Premium",
    },
    {
      id: "option_month",
      accessorFn: (row: OtcErrorCorrectionDto) => row.option_month,
      header: "Option Month",
    },
    {
      id: "date",
      accessorFn: (row: OtcErrorCorrectionDto) => row.date,
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v: unknown) =>
        moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
      filterParseDateFn: (v: unknown) =>
        moment.tz(v as string, CommonDestinationTimezone),
      header: "Date",
      cell: ({ row }: { row: { original: OtcErrorCorrectionDto } }) => (
        <span className="text-gray-600 text-sm">
          {moment
            .tz(row.original.date, CommonDestinationTimezone)
            .format("MM/DD/YYYY")}
        </span>
      ),
    },
    {
      id: "trader",
      accessorFn: (row: OtcErrorCorrectionDto) =>
        row.trader_id ? tradersById.get(row.trader_id) ?? "" : "",
      header: "Trader",
    },
    {
      id: "notes",
      accessorFn: (row: OtcErrorCorrectionDto) => row.notes ?? "",
      header: "Notes",
    },
    {
      id: "created_by",
      accessorFn: (row: OtcErrorCorrectionDto) =>
        row.CreatedByUser?.name ?? "",
      header: "Created By",
    },
    {
      id: "updated_by",
      accessorFn: (row: OtcErrorCorrectionDto) =>
        row.UpdatedByUser?.name ?? "",
      header: "Updated By",
    },
    {
      id: "created_at",
      accessorFn: (row: OtcErrorCorrectionDto) => row.created_at,
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v: unknown) =>
        moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
      filterParseDateFn: (v: unknown) =>
        moment.tz(v as string, CommonDestinationTimezone),
      header: "Created At",
      cell: ({ row }: { row: { original: OtcErrorCorrectionDto } }) => (
        <span className="text-gray-600 text-sm">
          {moment
            .tz(row.original.created_at, CommonDestinationTimezone)
            .format("MM/DD/YYYY")}
        </span>
      ),
    },
  ];
}

export function ErrorCorrectionsSection() {
  const errorCorrectionsRes = useListOtcErrorCorrections();
  const tradersRes = useListTraders();
  const accountsRes = useListAccounts(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const errorCorrections = errorCorrectionsRes?.data?.data || [];
  const traders = tradersRes?.data?.data || [];
  const accounts = accountsRes?.data?.data || [];
  const isLoading = errorCorrectionsRes?.isLoading;

  const tradersById = new Map<string, string>(
    traders.map((trader: Trader): [string, string] => [
      trader.id,
      trader.name || "",
    ]),
  );

  // Derive the selected row from live query data so it stays fresh after an
  // update invalidates the list query.
  const selected = selectedId
    ? errorCorrections.find(
        (row: OtcErrorCorrectionDto) => row.id === selectedId,
      ) ?? null
    : null;

  const columns = buildColumns(tradersById);

  return (
    <div className="w-full">
      <div className="flex flex-row justify-start w-full items-center my-2">
        <Button type="button" onClick={() => setCreateOpen(true)}>
          Create Error Correction
        </Button>
      </div>

      <CreateErrorCorrectionModal
        open={createOpen}
        traders={traders}
        accounts={accounts}
        onClose={() => setCreateOpen(false)}
        onCreated={() => setCreateOpen(false)}
      />

      <UpdateErrorCorrectionModal
        open={selectedId !== null}
        row={selected}
        traders={traders}
        accounts={accounts}
        onClose={() => setSelectedId(null)}
        onUpdated={() => setSelectedId(null)}
        onDeleted={() => setSelectedId(null)}
      />

      <DataTable
        stickyHeaders
        tableClassName="max-h-[calc(90vh-400px)] overflow-y-scroll"
        data={errorCorrections}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        enableVirtualization
        virtualizationRowHeight={48}
        virtualizationOverscan={2}
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "created_at", desc: true }]}
        emptyMessage={isLoading ? "Loading..." : "No error corrections"}
        rowClickable={true}
        onRowClick={(row: OtcErrorCorrectionDto) => setSelectedId(row.id)}
      />
    </div>
  );
}
